import {
    api,
    apiErrorMessage,
    businessTimezone,
    formatDay,
    getTimezone,
    oneSpec,
    toast,
    todayInTz,
} from '@bemor/web-kit';
import {
    AsOfBadge,
    BusinessDayLabel,
    Button,
    DateRange,
    DownloadIcon,
    PageHeader,
    SearchableMultiSelect,
    SearchableSelect,
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
    type DateRangeValue,
    type SelectOption,
} from '@bemor/web-kit/ui';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { FinanceReport, Merchant, Store } from '@/api/types';
import { useOne } from '@/lib/queries';
import { FinanceCharts } from '@/pages/reports/finance-charts';
import { FinanceKpiCards } from '@/pages/reports/finance-kpi-cards';
import { FinanceTable } from '@/pages/reports/finance-table';

/* BM-122 (Nexroll `profit-report/index.tsx`): the finance report: a period (business days of the tenant's zone), a
   grouping and a dimension in the URL, the KPI tiles, the Table and Chart tabs, the CSV. */

const PATH = '/staff/reports/finance';
const GROUPS: SelectOption[] = [
    { value: 'day', label: 'By day' },
    { value: 'week', label: 'By week' },
    { value: 'month', label: 'By month' },
];
const BY: SelectOption[] = [
    { value: 'period', label: 'Period' },
    { value: 'merchant', label: 'Merchant' },
    { value: 'store', label: 'Store' },
    { value: 'card_brand', label: 'Card brand' },
    { value: 'fee_rule', label: 'Fee rule' },
];

/** The report's query as the page reads it from the URL: the business month to date by default. */
function financeQuery(params: URLSearchParams) {
    const today = todayInTz(businessTimezone());
    return {
        from: params.get('from') ?? today.slice(0, 8) + '01',
        to: params.get('to') ?? today,
        group: params.get('group') ?? 'month',
        by: params.get('by') ?? 'period',
        merchant_id: params.getAll('merchant_id'),
        store_id: params.getAll('store_id'),
    };
}

/** The reads of `/reports/finance`, shared with the route's prefetch. */
export const financeReads = (url: URL) => {
    const q = financeQuery(url.searchParams);
    return {
        report: oneSpec(PATH, q),
    };
};

const loadMerchants = async (query: string): Promise<SelectOption[]> =>
    (
        await api().get<Merchant[]>('/staff/merchants', {
            params: { q: query, per_page: 20 },
        })
    ).map((m) => ({
        value: m.id,
        label: m.display_name ?? m.legal_name ?? m.id,
    }));

const loadStores = async (query: string): Promise<SelectOption[]> =>
    (
        await api().get<Store[]>('/staff/stores', {
            params: { q: query, per_page: 20 },
        })
    ).map((s) => ({
        value: s.id,
        label: s.display_name ?? s.name ?? s.id,
    }));

export function FinancePage() {
    const [params, setParams] = useSearchParams();
    const q = financeQuery(params);
    const set = (patch: Record<string, string | string[] | null>) => {
        const next = new URLSearchParams(params);
        for (const [key, value] of Object.entries(patch)) {
            next.delete(key);
            if (Array.isArray(value)) value.forEach((v) => next.append(key, v));
            else if (value) next.set(key, value);
        }
        setParams(next, { replace: true });
    };
    const report = useOne<FinanceReport>(PATH, q);
    // the Chart tab's two series: per period and per merchant over the same range and scope
    const [tab, setTab] = useState('table');
    const chartScope = {
        from: q.from,
        to: q.to,
        merchant_id: q.merchant_id,
        store_id: q.store_id,
    };
    const byPeriod = useOne<FinanceReport>(tab === 'chart' ? PATH : null, {
        ...chartScope,
        group: q.group,
        by: 'period',
    });
    const byMerchant = useOne<FinanceReport>(tab === 'chart' ? PATH : null, {
        ...chartScope,
        group: 'month',
        by: 'merchant',
    });
    const [busy, setBusy] = useState(false);
    const exportUrl = `/api${PATH}/export?${new URLSearchParams(
        Object.entries(q).flatMap(([k, v]) =>
            Array.isArray(v) ? v.map((x) => [`${k}[]`, x]) : [[k, v]],
        ),
    ).toString()}`;
    const download = async () => {
        setBusy(true);
        try {
            const response = await fetch(exportUrl, {
                headers: { Accept: 'text/csv' },
                credentials: 'same-origin',
            });
            if (!response.ok) {
                toast.error('The export failed. Try again.');
                return;
            }
            const href = URL.createObjectURL(await response.blob());
            const a = document.createElement('a');
            a.href = href;
            a.download = `finance-${q.from}-${q.to}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(href);
            toast.success('Export downloaded');
        } catch {
            toast.error('The export failed. Try again.');
        } finally {
            setBusy(false);
        }
    };
    const data = report.data?.data;
    const range: DateRangeValue = { from: q.from, to: q.to };
    return (
        <div className="flex flex-col gap-4">
            <PageHeader
                title="Finance"
                meta={
                    <span className="flex flex-col gap-1">
                        <BusinessDayLabel
                            zone={data?.zone ?? businessTimezone()}
                            viewerZone={getTimezone()}
                            prefix={`${formatDay(q.from)} to ${formatDay(q.to)}`}
                        />
                        <AsOfBadge asOf={report.freshness.asOf} />
                    </span>
                }
                secondaryAction={
                    <Button
                        variant="outline"
                        onClick={() => void download()}
                        loading={busy}
                        data-testid="finance-export"
                        data-href={exportUrl}
                    >
                        <DownloadIcon className="size-4" aria-hidden="true" />
                        Export CSV
                    </Button>
                }
            />
            <div
                className="grid grid-cols-1 gap-3 md:grid-cols-5"
                data-testid="finance-filters"
            >
                <DateRange
                    label="Period"
                    value={range}
                    onChange={(v) =>
                        v && 'from' in v
                            ? set({ from: v.from, to: v.to })
                            : set({ from: null, to: null })
                    }
                    timeZone={businessTimezone()}
                />
                <SearchableSelect
                    label="Group"
                    options={GROUPS}
                    value={q.group}
                    onChange={(v) => set({ group: v })}
                    testId="finance-group"
                />
                <SearchableSelect
                    label="Rows"
                    options={BY}
                    value={q.by}
                    onChange={(v) => set({ by: v })}
                    testId="finance-by"
                />
                <SearchableMultiSelect
                    label="Merchants"
                    noun="merchants"
                    loadOptions={loadMerchants}
                    value={q.merchant_id}
                    onChange={(v) => set({ merchant_id: v })}
                    testId="finance-merchants"
                />
                <SearchableMultiSelect
                    label="Stores"
                    noun="stores"
                    loadOptions={loadStores}
                    value={q.store_id}
                    onChange={(v) => set({ store_id: v })}
                    testId="finance-stores"
                />
            </div>
            {report.error ? (
                <p role="alert" className="text-danger-fg text-sm">
                    {apiErrorMessage(report.error)}
                </p>
            ) : null}
            <FinanceKpiCards totals={data?.totals} loading={report.isPending} />
            <Tabs value={tab} onValueChange={setTab}>
                <TabsList>
                    <TabsTrigger value="table">Table</TabsTrigger>
                    <TabsTrigger value="chart">Chart</TabsTrigger>
                </TabsList>
                <TabsContent value="table">
                    {data ? <FinanceTable report={data} by={q.by} /> : null}
                </TabsContent>
                <TabsContent value="chart">
                    <FinanceCharts
                        byPeriod={byPeriod.data?.data}
                        byMerchant={byMerchant.data?.data}
                    />
                </TabsContent>
            </Tabs>
        </div>
    );
}
